import { CommonModule } from '@angular/common'
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core'
import {
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete'
import { MatChipsModule } from '@angular/material/chips'
import { MatIcon } from '@angular/material/icon'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'

import {
  Observable,
  catchError,
  debounceTime,
  filter,
  finalize,
  map,
  of,
  startWith,
  switchMap,
  tap,
} from 'rxjs'

import {
  Item,
  MultipleSelectComponent,
} from '../../../../components/multiple-select/multiple-select.component'
import {
  Option,
  SelectComponent,
} from '../../../../components/select/select.component'
import {
  DialogResult,
  MessageDialogService,
} from '../../../../components/message-dialog/message-dialog.service'
import {
  invalidStringValidator,
  requiredArrayValidator,
} from '../../../../directives/validator/custom-validator'
import {
  User,
  UserForAdmin,
  UserPhoneNumber,
} from '../../../../services/user/user.mapping'
import { EMAIL_REGEXP } from '../../../../services/user/user.service'
import { UserStore } from '../../../../stores/user.store'
import {
  CustomError,
  CustomErrorCode,
} from '../../../../util/throwIfCustomError'
import { isFeatureAllowed } from '../../../../config/feature-permission.config'

const USER_PHONE_NUMBER_IDENTIFIER_REGEX = /^(VoicerecOwner|[0-9]+)$/

interface UserEditModel {
  name: string
  email: string
  departmentName: string
  reportToEmail: string
  roleCodes: string[]
  licenseId: string | null
  requiredMfa: boolean
}

@UntilDestroy()
@Component({
  selector: 'app-admin-user-form',
  standalone: true,
  templateUrl: './admin-user-form.component.html',
  styleUrls: ['./admin-user-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ReactiveFormsModule,
    CommonModule,
    MatAutocompleteModule,
    MatIcon,
    MatChipsModule,
    MatCheckboxModule,
    MultipleSelectComponent,
    SelectComponent,
  ],
})
export class AdminUserFormComponent implements OnInit, OnChanges {
  @Input() roleEditable = true
  @Input() emailEditable = true
  @Input() departmentNames: string[] = []
  @Input() isEdit: boolean = false
  @Input() user: UserForAdmin | null = null
  @Input() roles: { name: string; code: string }[] = []
  @Input() selectableLicenses: Option[] = []
  @Input() selectedOption!: Option
  @Output() validChanges = new EventEmitter<boolean>()

  name = new FormControl()
  email = new FormControl()
  departmentName = new FormControl()
  reportTo = new FormControl()
  reportToInput = new FormControl()
  roleCodes = new FormControl()
  requiredMfa = new FormControl()
  phoneNumber = new FormControl('', [
    Validators.required,
    Validators.pattern(USER_PHONE_NUMBER_IDENTIFIER_REGEX),
  ])

  roleItems: Item[] = []
  phoneNumbers: UserPhoneNumber[] = []
  phoneNumberErrorMessage = ''
  isPhoneNumberLoading = false

  isMine = false

  filteredDepartmentNames: Observable<string[]> = of([])
  filteredUsers: Observable<User[]> = of([])

  private formGroup = this.formBuilder.group({
    name: this.name,
    email: this.email,
    departmentName: this.departmentName,
    reportTo: this.reportTo,
    roleCodes: this.roleCodes,
    requiredMfa: this.requiredMfa,
  })

  constructor(
    private formBuilder: FormBuilder,
    private userStore: UserStore,
    private messageDialogService: MessageDialogService,
  ) {}

  get canSeeUserPhoneNumber(): boolean {
    return isFeatureAllowed(
      'KAIKURA_INTEGRATION',
      this.userStore.ctxUser()?.enabledFeatures,
    )
  }

  ngOnInit(): void {
    const isAdmin = this.userStore.isAdmin()
    this.isMine = this.user?.id === this.userStore.ctxUser()?.id
    this.roleItems = this.roles.map((role) => {
      const notSelectableEnabler = !isAdmin && role.code === 'SUPER_ADMIN'
      const notSelectableMine =
        role.code === 'SUPER_ADMIN' &&
        this.user?.id === this.userStore.ctxUser()?.id
      return {
        label: role.name,
        id: role.code,
        checked: false,
        notSelectable: notSelectableEnabler || notSelectableMine,
      }
    })
    if (this.user) {
      this.name.setValue(this.user.name)
      this.email.setValue(this.user.email)
      this.departmentName.setValue(this.user.departmentName)
      this.reportTo.setValue(this.user.reportTo)
      this.user.roles.forEach((role) => {
        const item = this.roleItems.find((r) => r.id === role.code)
        if (item) {
          item.checked = true
        }
      })
      this.roleCodes.setValue(this.user.roles.map((role) => role.code))
      this.requiredMfa.setValue(this.user.requiredMfa)
      this.phoneNumbers = [...this.user.phoneNumbers]
    }
    this.name.setValidators(Validators.required)
    this.email.setValidators([
      Validators.required,
      invalidStringValidator(EMAIL_REGEXP),
    ])
    this.departmentName.setValidators(Validators.required)
    this.roleCodes.setValidators(requiredArrayValidator())
    this.roleCodes.updateValueAndValidity()

    this.filteredDepartmentNames = this.departmentName.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      switchMap((val) => {
        return of(
          this.departmentNames.filter((depName) =>
            depName.toLocaleLowerCase().includes(val.toLocaleLowerCase()),
          ),
        )
      }),
    )

    this.filteredUsers = this.reportToInput.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      switchMap((val) => {
        return this.userStore
          .searchUsers(
            val ?? '',
            [],
            {
              limit: 50,
              offset: 0,
            },
            { sortField: 'name', sortOrder: 'asc' },
            false,
          )
          .pipe(
            map((users) => {
              return users.filter((user) => user.email !== this.email.value)
            }),
          )
      }),
    )

    // ngOnInit 完了時点で formGroup の status が既に VALID で安定していると、
    // 以降 statusChanges が emit されず親の inputValid が false のまま保存ボタンが
    // 永続 disabled になる。startWith で初期 status を 1 回流して同期させる。
    this.formGroup.statusChanges
      .pipe(
        startWith(this.formGroup.status),
        untilDestroyed(this),
        tap((status) => this.validChanges.next(status === 'VALID')),
      )
      .subscribe()
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.user?.currentValue) {
      const user = changes.user.currentValue
      this.formGroup.setValue({
        name: user.name ?? '',
        email: user.email ?? '',
        departmentName: user.departmentName ?? '',
        reportTo: user.reportTo ?? '',
        roleCodes: user.roles.map((role: { code: string }) => role.code) ?? [],
        requiredMfa: user.requiredMfa ?? false,
      })
      this.phoneNumbers = [...user.phoneNumbers]
    }
  }

  reportToOptionSelected(event: MatAutocompleteSelectedEvent): void {
    this.reportToInput.setValue(null)
    this.reportTo.setValue(event.option.value)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const elem = (this.reportToInput as any).nativeElement
    if (elem) {
      elem.value = ''
    }
  }

  reportToInputBlur(): void {
    this.reportToInput.setValue(null)
  }

  getInputUser = (): UserEditModel => {
    return {
      name: this.name.value ?? '',
      email: this.email.value ?? '',
      departmentName: this.departmentName.value ?? '',
      reportToEmail: this.reportTo.value?.email ?? '',
      roleCodes: this.roleItems
        .filter((role) => role.checked)
        .map((role) => role.id ?? ''),
      licenseId: this.selectedOption.id ? this.selectedOption.id : null,
      requiredMfa: this.requiredMfa.value,
    }
  }

  changeSelectedRoles(items: Item[]): void {
    this.roleItems = items
    this.roleCodes.setValue(
      items.filter((role) => role.checked).map((role) => role.id),
    )
    this.roleCodes.updateValueAndValidity()
  }

  selectedOptionChange(option: Option): void {
    this.selectedOption = option
    this.formGroup.updateValueAndValidity()
  }

  addPhoneNumber(): void {
    if (!this.user) {
      return
    }

    this.phoneNumberErrorMessage = ''
    if (this.phoneNumber.invalid) {
      this.phoneNumber.markAsTouched()
      return
    }

    const phoneNumber = this.phoneNumber.value
    if (!phoneNumber) {
      this.phoneNumber.markAsTouched()
      return
    }

    this.isPhoneNumberLoading = true
    this.userStore
      .addUserPhoneNumber(this.user.id, phoneNumber)
      .pipe(
        tap((addedPhoneNumber) => {
          this.phoneNumbers = [...this.phoneNumbers, addedPhoneNumber]
          this.phoneNumber.reset('')
          this.phoneNumber.markAsUntouched()
        }),
        catchError((error: unknown) => {
          if (
            error instanceof CustomError &&
            error.code === CustomErrorCode.DUPLICATE_USER_PHONE_NUMBER
          ) {
            this.phoneNumberErrorMessage =
              'この番号は既に別ユーザに登録されています'
          } else {
            this.phoneNumberErrorMessage =
              '電話番号の追加に失敗しました。時間をおいて再度お試しください'
          }
          return of()
        }),
        finalize(() => {
          this.isPhoneNumberLoading = false
        }),
        untilDestroyed(this),
      )
      .subscribe()
  }

  deletePhoneNumber(phoneNumber: UserPhoneNumber): void {
    this.messageDialogService
      .showConfirm(
        `${phoneNumber.phoneNumber} を削除します。よろしいですか？この番号で着信した通話はアセスメント対象外になります。`,
        {
          primaryButtonText: '削除',
          secondaryButtonText: 'キャンセル',
        },
      )
      .pipe(
        filter((result) => result === DialogResult.PrimaryButtonClicked),
        tap(() => {
          this.phoneNumberErrorMessage = ''
          this.isPhoneNumberLoading = true
        }),
        switchMap(() =>
          this.userStore.deleteUserPhoneNumber(phoneNumber.id).pipe(
            tap(() => {
              this.phoneNumbers = this.phoneNumbers.filter(
                (item) => item.id !== phoneNumber.id,
              )
            }),
            catchError(() => {
              this.phoneNumberErrorMessage =
                '電話番号の削除に失敗しました。時間をおいて再度お試しください'
              return of()
            }),
            finalize(() => {
              this.isPhoneNumberLoading = false
            }),
          ),
        ),
        untilDestroyed(this),
      )
      .subscribe()
  }

  showPhoneNumberError(): boolean {
    return (
      this.phoneNumberErrorMessage.length > 0 ||
      (this.phoneNumber.touched && this.phoneNumber.invalid)
    )
  }

  phoneNumberValidationMessage(): string {
    if (this.phoneNumberErrorMessage) {
      return this.phoneNumberErrorMessage
    }
    if (this.phoneNumber.hasError('required')) {
      return '電話番号は必須です'
    }
    if (this.phoneNumber.hasError('pattern')) {
      return '数字のみ、または VoicerecOwner（対面録音用）を入力してください'
    }
    return 'エラー'
  }

  get phoneNumberInputId(): string {
    return this.user ? `user-phone-number-${this.user.id}` : 'user-phone-number'
  }
}
